import type { ValidationSummary } from '@finapp/types';
import type React from 'react';

interface DraftProgressBarProps {
  validationSummary: ValidationSummary;
}

export const DraftProgressBar: React.FC<DraftProgressBarProps> = ({ validationSummary }) => {
  const { total_transactions, complete_transactions, incomplete_transactions, total_errors } =
    validationSummary;

  const completionPercentage =
    total_transactions > 0 ? (complete_transactions / total_transactions) * 100 : 0;

  const hasErrors = total_errors > 0;

  return (
    <div className="space-y-3">
      {/* Progress Bar */}
      <div className="w-full">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">
            Progress: {complete_transactions} of {total_transactions} transactions complete
          </span>
          <span className="text-sm text-gray-500">{Math.round(completionPercentage)}%</span>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full transition-all duration-300 ${
              hasErrors
                ? 'bg-red-500'
                : completionPercentage === 100
                  ? 'bg-green-500'
                  : 'bg-blue-500'
            }`}
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>

      {/* Status Indicators */}
      <div className="flex items-center space-x-6 text-sm">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded-full mr-2" />
          <span className="text-gray-700">{complete_transactions} Complete</span>
        </div>

        {incomplete_transactions > 0 && (
          <div className="flex items-center">
            <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2" />
            <span className="text-gray-700">{incomplete_transactions} Incomplete</span>
          </div>
        )}

        {total_errors > 0 && (
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-2" />
            <span className="text-gray-700">
              {total_errors} Error{total_errors !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Completion Message */}
      {completionPercentage === 100 && total_errors === 0 && (
        <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-md">
          <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm font-medium text-green-800">
            All transactions are complete and ready to commit!
          </span>
        </div>
      )}
    </div>
  );
};
